import { pick, without, extend, isEmpty } from 'underscore';
import { ScopeLevel } from '@bingads-webui-campaign-react/constants';
import {
  modelToViewModelForAdGroupAgeTargets,
  modelToViewModelForAdGroupCompanyNameTargets,
  modelToViewModelForAdGroupIndustryTargets,
  modelToViewModelForAdGroupJobFunctionTargets,
  modelToViewModelForAdGroupLocationTargets,
  bulkModelToViewModelForAdGroupLocationTargets,
  bulkModelToViewModelForAdGroupAgeTargets,
  bulkModelToViewModelForAdGroupGenderTargets,
  bulkModelToViewModelForAdGroupDeviceTargets,
} from './adgroup-criterion';
import {
  modelToViewModelForDayTimeTargets,
  bulkModelToViewModelForDayTimeTargets,
  modelToViewModelForDeviceTargets,
  modelToViewModelForGenderTargets,
  modelToViewModelForAgeTargets,
  modelToViewModelForCompanyNameTargets,
  modelToViewModelForIndustryTargets,
  modelToViewModelForJobFunctionTargets,
} from './campaign-criterion';
import { bulkModelToViewModelForDsaTarget } from './dsa-target';
import { parseDateFromAPIFormat, formatDateToAPIFormat } from './utils';
import { modelToViewModelForMultiMediaBidAdjustment, viewModelToModelForMultiMediaAdBidAdjustment } from './adjustment';
import { modelToViewModelForAdRotation } from './ad-rotation';

const whitelistedSchemaPropertyKeysForCampaignModel = [
  'Id', 'BiddingScheme', 'AdScheduleUseSearcherTimeZone', 'TimeZone', 'MultiMediaAdBidAdjustment', 'Languages',
];

const whitelistedSchemaPropertyKeysForAdGroupModel = [
  'Id', 'Name', 'Status', 'AdGroupType', 'Language', 'StartDate', 'EndDate', 'SearchBid', 'Medium', 'AdDistribution', 'AdRotation', 'AdGroupType', 'AdScheduleUseSearcherTimeZone', 'MultiMediaAdBidAdjustment', 'BiddingScheme', 'TrackingUrlTemplate', 'FinalUrlSuffix', 'UrlCustomParameters',
];

const viewModelToModelForUrlCustomParameters = urlCustomParametersViewModel =>
  urlCustomParametersViewModel.filter(({ Key }) => Key);

export const modelToViewModelForAdGroupSetting = ({
  adGroupModel, campaignModel, campaignScheduleCriterionModel, adGroupScheduleCriterionModel, campaignTargetCriterionModel, adGroupTargetCriterionModel, bundleCountries, adGroupLocationTargets, campaignLocationTargets, i18n,
}) => {
  let campaignViewModel = {
    ...pick(campaignModel, whitelistedSchemaPropertyKeysForCampaignModel),
  };

  let adGroupViewModel = {
    ...pick(adGroupModel, whitelistedSchemaPropertyKeysForAdGroupModel),
  };

  adGroupViewModel.currentSelectedDeviceLevel = !isEmpty(adGroupTargetCriterionModel.filter(target => target.Criterion['@odata.type'] === '#Model.DeviceCriterion')) ? 'adgroup' : 'campaign';

  adGroupViewModel.currentSelectedGenderLevel = !isEmpty(adGroupTargetCriterionModel.filter(target => target.Criterion['@odata.type'] === '#Model.GenderCriterion')) ? 'adgroup' : 'campaign';

  adGroupViewModel.currentSelectedAgeLevel = !isEmpty(adGroupTargetCriterionModel.filter(target => target.Criterion['@odata.type'] === '#Model.AgeCriterion')) ? 'adgroup' : 'campaign';

  adGroupViewModel.currentSelectedCompanyNameLevel = !isEmpty(adGroupTargetCriterionModel.filter(target => target.Criterion['@odata.type'] === '#Model.CompanyNameCriterion')) ? 'adgroup' : 'campaign';

  adGroupViewModel.currentSelectedIndustryLevel = !isEmpty(adGroupTargetCriterionModel.filter(target => target.Criterion['@odata.type'] === '#Model.IndustryCriterion')) ? 'adgroup' : 'campaign';

  adGroupViewModel.currentSelectedJobFunctionLevel = !isEmpty(adGroupTargetCriterionModel.filter(target => target.Criterion['@odata.type'] === '#Model.JobFunctionCriterion')) ? 'adgroup' : 'campaign';

  campaignViewModel = extend(campaignViewModel, modelToViewModelForDeviceTargets({
    model: campaignTargetCriterionModel.filter(target => target.Criterion['@odata.type'] === '#Model.DeviceCriterion'),
    campaignId: campaignModel.Id,
  }));

  campaignViewModel = extend(campaignViewModel, modelToViewModelForGenderTargets({
    model: campaignTargetCriterionModel.filter(target => target.Criterion['@odata.type'] === '#Model.GenderCriterion'),
    campaignId: campaignModel.Id,
  }));

  campaignViewModel = extend(campaignViewModel, modelToViewModelForAgeTargets({
    model: campaignTargetCriterionModel.filter(target => target.Criterion['@odata.type'] === '#Model.AgeCriterion'),
    campaignId: campaignModel.Id,
  }));

  campaignViewModel = extend(campaignViewModel, modelToViewModelForCompanyNameTargets({
    model: campaignTargetCriterionModel.filter(target => target.Criterion['@odata.type'] === '#Model.CompanyNameCriterion'),
    campaignId: campaignModel.Id,
  }));

  campaignViewModel = extend(campaignViewModel, modelToViewModelForIndustryTargets({
    model: campaignTargetCriterionModel.filter(target => target.Criterion['@odata.type'] === '#Model.IndustryCriterion'),
    campaignId: campaignModel.Id,
  }));

  campaignViewModel = extend(campaignViewModel, modelToViewModelForJobFunctionTargets({
    model: campaignTargetCriterionModel.filter(target => target.Criterion['@odata.type'] === '#Model.JobFunctionCriterion'),
    campaignId: campaignModel.Id,
  }));

  campaignViewModel.dayTimeRanges = campaignScheduleCriterionModel ? modelToViewModelForDayTimeTargets(campaignScheduleCriterionModel) : [];
  adGroupViewModel.dayTimeRanges = adGroupScheduleCriterionModel ? modelToViewModelForDayTimeTargets(adGroupScheduleCriterionModel) : [];

  adGroupViewModel.StartDate = parseDateFromAPIFormat(i18n, adGroupModel.StartDate);
  adGroupViewModel.EndDate = parseDateFromAPIFormat(i18n, adGroupModel.EndDate);

  adGroupViewModel = extend(adGroupViewModel, modelToViewModelForAdGroupLocationTargets({
    model: adGroupLocationTargets,
    campaignLocationModel: campaignLocationTargets,
    bundleCountries,
    adGroupId: adGroupViewModel.Id,
    campaignId: campaignModel.Id,
  }));

  adGroupViewModel = extend(adGroupViewModel, modelToViewModelForMultiMediaBidAdjustment(ScopeLevel.AdGroup, campaignModel.MultiMediaAdBidAdjustment, adGroupViewModel.MultiMediaAdBidAdjustment));

  adGroupViewModel = extend(adGroupViewModel, modelToViewModelForDeviceTargets({
    model: adGroupTargetCriterionModel.filter(target => target.Criterion['@odata.type'] === '#Model.DeviceCriterion'),
    campaignId: campaignModel.Id,
  }));

  adGroupViewModel = extend(adGroupViewModel, modelToViewModelForGenderTargets({
    model: adGroupTargetCriterionModel.filter(target => target.Criterion['@odata.type'] === '#Model.GenderCriterion'),
    campaignId: campaignModel.Id,
  }));

  adGroupViewModel = extend(adGroupViewModel, modelToViewModelForAdGroupAgeTargets({
    model: adGroupTargetCriterionModel.filter(target => target.Criterion['@odata.type'] === '#Model.AgeCriterion'),
    campaignId: campaignModel.Id,
    adGroupId: adGroupViewModel.Id,
  }));

  adGroupViewModel = extend(adGroupViewModel, modelToViewModelForAdGroupCompanyNameTargets({
    model: adGroupTargetCriterionModel.filter(target => target.Criterion['@odata.type'] === '#Model.CompanyNameCriterion'),
    campaignViewModel,
    currentLevel: adGroupViewModel.currentSelectedIndustryLevel,
  }));

  adGroupViewModel = extend(adGroupViewModel, modelToViewModelForAdGroupIndustryTargets({
    model: adGroupTargetCriterionModel.filter(target => target.Criterion['@odata.type'] === '#Model.IndustryCriterion'),
    campaignViewModel,
    currentLevel: adGroupViewModel.currentSelectedIndustryLevel,
  }));

  adGroupViewModel = extend(adGroupViewModel, modelToViewModelForAdGroupJobFunctionTargets({
    model: adGroupTargetCriterionModel.filter(target => target.Criterion['@odata.type'] === '#Model.JobFunctionCriterion'),
    campaignViewModel,
    currentLevel: adGroupViewModel.currentSelectedJobFunctionLevel,
  }));

  adGroupViewModel = extend(adGroupViewModel, modelToViewModelForAdRotation(adGroupViewModel));

  return { campaignViewModel, adGroupViewModel };
};

export const viewModelToModelForAdGroupSetting = (adGroupViewModel, campaignId, i18n, permissions) => {
  let data = {
    ...pick(adGroupViewModel, without(whitelistedSchemaPropertyKeysForAdGroupModel, 'Status', 'BiddingScheme')),
    Campaign: {
      Id: campaignId,
    },
  };

  if (!permissions.IsDomesticSimplifiedChineseEnabled || (permissions.IsDomesticSimplifiedChineseEnabled && permissions.dynamic.MMAForCN)) {
    data = extend(data, viewModelToModelForMultiMediaAdBidAdjustment(ScopeLevel.AdGroup, adGroupViewModel, i18n));
  } else {
    delete data.MultimediaAdBidAdjustment;
    delete data.MultiMediaAdBidAdjustment;
    if (data.Language === null) {
      delete data.Language;
    }
  }

  if (adGroupViewModel.Status === 'Active') {
    data.Status = 'Active';
  }
  data.StartDate = formatDateToAPIFormat(i18n, data.StartDate);
  data.EndDate = data.EndDate ? formatDateToAPIFormat(i18n, data.EndDate) : '1/1/3000';

  if (adGroupViewModel.UrlCustomParameters) {
    data.UrlCustomParameters = viewModelToModelForUrlCustomParameters(adGroupViewModel.UrlCustomParameters);
  }

  return data;
};

export const bulkModelToViewModelForAdGroupSetting = ({
  adGroupModel,
  originaladGroupModel,
  campaignModel,
  bundleCountries,
  i18n,
}) => {
  let data = {
    ...pick(adGroupModel, whitelistedSchemaPropertyKeysForAdGroupModel),
  };
  data.StartDate = parseDateFromAPIFormat(i18n, adGroupModel.StartDate);
  data.EndDate = parseDateFromAPIFormat(i18n, adGroupModel.EndDate);

  if (adGroupModel.AdGroupCriterions && !isEmpty(adGroupModel.AdGroupCriterions)) {
    data.dayTimeRanges = bulkModelToViewModelForDayTimeTargets(adGroupModel.AdGroupCriterions, originaladGroupModel.dayTimeRanges || []);
    data.adSchedulesBulkUpsert = [];
  }

  data.Device = originaladGroupModel.Device && bulkModelToViewModelForAdGroupDeviceTargets(originaladGroupModel.Device, adGroupModel.AdGroupCriterions.filter(target => target.Criterion['@odata.type'] === '#Model.DeviceCriterion'));
  data.initialDevice = originaladGroupModel.initialDevice;
  data.currentSelectedDeviceLevel = originaladGroupModel.currentSelectedDeviceLevel;

  data.Gender = originaladGroupModel.Gender && bulkModelToViewModelForAdGroupGenderTargets(originaladGroupModel.Gender, adGroupModel.AdGroupCriterions.filter(target => target.Criterion['@odata.type'] === '#Model.GenderCriterion'));
  data.initialGender = originaladGroupModel.initialGender;
  data.currentSelectedGenderLevel = originaladGroupModel.currentSelectedGenderLevel;

  data.Age = originaladGroupModel.Age && bulkModelToViewModelForAdGroupAgeTargets(originaladGroupModel.Age, adGroupModel.AdGroupCriterions.filter(target => target.Criterion['@odata.type'] === '#Model.AgeCriterion'));
  data.initialAge = originaladGroupModel.initialAge;
  data.currentSelectedAgeLevel = originaladGroupModel.currentSelectedAgeLevel;

  data.companyNameTargets = originaladGroupModel.currentSelectedIndustryLevel === 'campaign' ? campaignModel.companyNameTargets : originaladGroupModel.companyNameTargets;
  data.initialCompanyNameTargets = originaladGroupModel.initialCompanyNameTargets;
  data.currentSelectedCompanyNameLevel = originaladGroupModel.currentSelectedCompanyNameLevel;

  data.industry = originaladGroupModel.currentSelectedIndustryLevel === 'campaign' ? campaignModel.industry : originaladGroupModel.industry;
  data.initialIndustryTargets = originaladGroupModel.currentSelectedIndustryLevel === 'campaign' ? campaignModel.initialIndustryTargets : originaladGroupModel.initialIndustryTargets;
  data.initialIndustry = originaladGroupModel.initialIndustry;
  data.currentSelectedIndustryLevel = originaladGroupModel.currentSelectedIndustryLevel;

  data.jobTargets = originaladGroupModel.currentSelectedIndustryLevel === 'campaign' ? campaignModel.jobTargets : originaladGroupModel.jobTargets;
  data.initialJobFunctionTargets = originaladGroupModel.currentSelectedIndustryLevel === 'campaign' ? campaignModel.initialJobFunctionTargets : originaladGroupModel.initialJobFunctionTargets;
  data.initialJobTargets = originaladGroupModel.initialJobTargets;
  data.currentSelectedJobFunctionLevel = originaladGroupModel.currentSelectedJobFunctionLevel;

  data = extend(data, modelToViewModelForMultiMediaBidAdjustment(ScopeLevel.AdGroup, campaignModel.MultiMediaAdBidAdjustment, adGroupModel.MultimediaAdBidAdjustment));

  if (adGroupModel.AdGroupCriterions && !isEmpty(adGroupModel.AdGroupCriterions)) {
    data = extend(data, bulkModelToViewModelForAdGroupLocationTargets(adGroupModel.AdGroupCriterions, bundleCountries, originaladGroupModel));
  }
  if (adGroupModel.DsaTargets) {
    data.DsaTargets = bulkModelToViewModelForDsaTarget(adGroupModel.DsaTargets, originaladGroupModel);
  }

  return data;
};
